import { useCallback, useEffect } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { Form, message } from 'antd';
import type { Rule } from 'antd/es/form';

import { KEYBOARD_KEYS } from '@/shared/lib/constants';

import { useAuthStore } from '@/entities/auth';

import { DEMO_ADMIN_PASSWORD, persistDemoAuthSession, tryDemoCredentialLogin } from '@/features/auth/lib/demoAuth';

export type LoginFormData = {
  username: string;
  password?: string;
};

const IS_DEMO_MODE = import.meta.env.MODE === 'demo';

/** 데모: 아이디만 검증(SSO 규격 이메일 등 비적용) */
export const demoUsernameRules: Rule[] = [{ required: true, message: '아이디를 입력해 주세요' }];

export const demoAdminPasswordRules: Rule[] = [
  { required: true, message: '비밀번호를 입력해 주세요' },
  { min: DEMO_ADMIN_PASSWORD.length, message: '비밀번호를 확인해 주세요' },
];

export const usernameRules: Rule[] = [
  { required: true, message: '사용자 ID를 입력해 주세요' },
  { min: 4, message: '사용자 ID는 최소 4자 이상이어야 합니다' },
  { max: 50, message: '사용자 ID는 최대 50자 이하이어야 합니다' },
  { whitespace: true, message: '사용자 ID에 공백을 포함할 수 없습니다' },
  {
    pattern: /^[a-zA-Z0-9!@#$%^&*()_+\-=\\[\]{};':"\\|,.<>\\/?]+$/,
    message: '!@#$%^&*()-_+=?/\\:;",.<>[]{} 외의 특수문자는 사용할 수 없습니다.',
  },
  {
    pattern: /^[a-zA-Z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    message: '사용자 ID는 이메일 형식이어야 합니다',
  },
];

export const passwordRules: Rule[] = [
  { required: true, message: '비밀번호를 입력해 주세요' },
  { min: 8, message: '비밀번호는 최소 8자 이상이어야 합니다' },
  { max: 20, message: '비밀번호는 최대 20자 이하이어야 합니다' },
  { whitespace: true, message: '비밀번호에 공백을 포함할 수 없습니다' },
  {
    pattern: /^[a-zA-Z0-9!@#$%^&*()_+\-=\\[\]{};':"\\|,.<>\\/?]+$/,
    message: '!@#$%^&*()-_+=?/\\:;",.<>[]{} 외의 특수문자는 사용할 수 없습니다.',
  },
];

export const useLoginFormField = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const setUser = useAuthStore((s) => s.setUser);
  const [form] = Form.useForm<LoginFormData>();

  const usernameWatch = Form.useWatch('username', form);
  const isAdminLogin = IS_DEMO_MODE && Boolean(usernameWatch && usernameWatch.trim().toLowerCase() === 'admin');

  useEffect(() => {
    if (!IS_DEMO_MODE || !form) {
      return;
    }

    if (!isAdminLogin) {
      form.setFieldValue('password', undefined);
    }
  }, [form, isAdminLogin]);

  const resolveDemoRedirectTarget = useCallback(() => {
    const from =
      typeof location.state === 'object' && location.state !== null && 'from' in location.state
        ? String((location.state as { from?: string }).from ?? '/')
        : '/';
    return from.startsWith('/') && !from.startsWith('/login') ? from : '/';
  }, [location.state]);

  const onSubmitDemo = useCallback(
    (values: LoginFormData) => {
      const trimmedUser = values.username.trim();
      const pwd = values.password ?? '';
      const profile = tryDemoCredentialLogin(trimmedUser, isAdminLogin ? pwd : undefined);

      if (!profile) {
        message.error(isAdminLogin ? '비밀번호가 올바르지 않습니다.' : '로그인에 실패했습니다.');
        return;
      }

      persistDemoAuthSession(profile);
      setUser(profile);
      message.success('로그인되었습니다.');
      void navigate(resolveDemoRedirectTarget(), { replace: true });
    },
    [isAdminLogin, navigate, resolveDemoRedirectTarget, setUser]
  );

  const onSubmitProductionStub = useCallback(async (_values: LoginFormData) => {
    console.warn('Direct login is disabled. Please use SSO login.');
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === KEYBOARD_KEYS.SPACE) {
      e.preventDefault();
    }
  }, []);

  return {
    form,
    onSubmit: IS_DEMO_MODE ? onSubmitDemo : onSubmitProductionStub,
    isLoading: false,
    handleKeyDown,
    isSubmitDisabled: !IS_DEMO_MODE,
    isDemoMode: IS_DEMO_MODE,
    isAdminLogin,
    appliedUsernameRules: IS_DEMO_MODE ? demoUsernameRules : usernameRules,
    /** 프로덕션 스텁: 항상 표시 · 데모: admin 일 때만 */
    showPasswordField: !IS_DEMO_MODE || isAdminLogin,
    passwordFieldRules: IS_DEMO_MODE && isAdminLogin ? demoAdminPasswordRules : passwordRules,
  };
};
